import initialize, { Decoder, Picture } from '@yume-chan/h264bsd';
import type { BinaryFrame } from '../transport/protocol';
import type { VideoRenderer } from './types';

/** Real baseline H.264 decoding into canvas. Never substitutes a synthetic picture for AA video. */
export class WasmH264Renderer implements VideoRenderer {
  private decoder: Decoder | null = null;
  private newDecoder: (() => Decoder) | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private pictures = 0;
  private fps = 0;
  private lastSample = Date.now();
  private sequence = 0;
  private image: ImageData | null = null;

  public async initialize(canvas: HTMLCanvasElement): Promise<void> {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false });
    const module = await initialize();
    this.newDecoder = () => new module.Decoder();
    this.decoder = this.newDecoder();
  }

  public renderFrame(frame: BinaryFrame): void {
    if (!this.decoder) return;
    let offset = 0;
    while (offset < frame.payload.length) {
      const result = this.decoder.decode(frame.payload.subarray(offset), this.sequence++);
      if (result.code >= 3) throw new Error(`WASM H.264 decode failed (${result.code}); baseline profile required`);
      if (result.picture) this.draw(result.picture);
      for (let i = 0; i < result.extraPictureCount; i++) {
        const picture = this.decoder.getNextPicture();
        if (picture) this.draw(picture);
      }
      if (result.read <= 0) break;
      offset += result.read;
    }
  }

  private draw(picture: Picture): void {
    if (!this.ctx || !this.canvas) return;
    const { picWidth: width, picHeight: height, cropParams, videoRange, matrixCoefficients } = picture.info;
    const crop = picture.info.croppingFlag !== 0;
    const outWidth = crop ? cropParams.cropOutWidth : width;
    const outHeight = crop ? cropParams.cropOutHeight : height;
    const left = crop ? cropParams.cropLeftOffset : 0;
    const top = crop ? cropParams.cropTopOffset : 0;
    if (this.canvas.width !== outWidth || this.canvas.height !== outHeight) {
      this.canvas.width = outWidth;
      this.canvas.height = outHeight;
    }
    if (!this.image || this.image.width !== outWidth || this.image.height !== outHeight) {
      this.image = this.ctx.createImageData(outWidth, outHeight);
    }
    const image = this.image;
    const lumaSize = width * height;
    const chromaStride = width / 2;
    const chromaSize = lumaSize / 4;
    // BT.601/709 and full/limited range are signalled in H.264 VUI.
    const kr = matrixCoefficients === 1 ? 0.2126 : 0.299;
    const kb = matrixCoefficients === 1 ? 0.0722 : 0.114;
    const kg = 1 - kr - kb;
    const full = videoRange !== 0;
    const rV = 2 - 2 * kr;
    const gU = kb * (2 - 2 * kb) / kg;
    const gV = kr * (2 - 2 * kr) / kg;
    const bU = 2 - 2 * kb;
    for (let y = 0; y < outHeight; y++) {
      for (let x = 0; x < outWidth; x++) {
        const sourceX = x + left;
        const sourceY = y + top;
        const chroma = (sourceY >> 1) * chromaStride + (sourceX >> 1);
        const luma = picture.bytes[sourceY * width + sourceX];
        const yy = full ? luma : (luma - 16) * 255 / 219;
        const uu = (picture.bytes[lumaSize + chroma] - 128) * (full ? 1 : 255 / 224);
        const vv = (picture.bytes[lumaSize + chromaSize + chroma] - 128) * (full ? 1 : 255 / 224);
        const target = (y * outWidth + x) * 4;
        image.data[target] = yy + rV * vv;
        image.data[target + 1] = yy - gU * uu - gV * vv;
        image.data[target + 2] = yy + bU * uu;
        image.data[target + 3] = 255;
      }
    }
    this.ctx.putImageData(image, 0, 0);
    this.pictures++;
    const now = Date.now();
    if (now - this.lastSample >= 1000) {
      this.fps = Math.round(this.pictures * 1000 / (now - this.lastSample));
      this.pictures = 0;
      this.lastSample = now;
    }
  }

  public reset(): void {
    this.decoder?.delete();
    this.decoder = this.newDecoder?.() ?? null;
    this.sequence = this.pictures = this.fps = 0;
    this.lastSample = Date.now();
    if (this.canvas) this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  public getFps(): number { return this.fps; }
  public getName(): string { return 'WASM H.264 Baseline Decoder'; }
  public destroy(): void {
    this.decoder?.delete();
    this.decoder = null;
    this.newDecoder = null;
    this.ctx = null;
    this.canvas = null;
    this.image = null;
  }
}
